import { TreeItem, TreeView } from "@material-ui/lab";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { Checkbox, CheckboxBase } from "~components/Checkbox";
import { Box, makeStyles, createStyles, Theme, FormLabel } from "@material-ui/core";
import React, { useState } from "react";

export type StatusFilterOption = {
  value: string;
  label: string;
  options?: StatusFilterOption[];
};

export type StatusFilterProps = {
  filter?: string[];
  setFilter?: (filters: { status: string[] }) => void;
  options: StatusFilterOption[];
};

function flattenValues(options: StatusFilterOption[]): string[] {
  return options
    .map((option) => (option.options ? flattenValues(option.options) : [option.value]))
    .reduce((acc, o) => acc.concat(o), []);
}

function findExpanded(selected: string[], options: StatusFilterOption[]): StatusFilterOption[] {
  let currentSelected = [...selected];
  return options.reduce(
    (acc: StatusFilterOption[], o: StatusFilterOption): StatusFilterOption[] => {
      if (Array.isArray(o.options)) {
        const newSelected = findExpanded(selected, o.options);
        currentSelected = currentSelected.concat(newSelected.map((o) => o.value));
        return [
          ...acc,
          ...(o.options.filter((option) => currentSelected.indexOf(option.value) !== -1).length > 0
            ? [o]
            : []),
          ...newSelected,
        ];
      }
      return acc;
    },
    [],
  );
}

export const StatusFilter = React.memo(({ options, setFilter, filter = [] }: StatusFilterProps) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState<string[]>(
    findExpanded(filter, options).map((o) => o.value),
  );

  const selectOption = (option: StatusFilterOption): void => {
    if (setFilter) {
      if (!Array.isArray(option.options)) {
        setFilter({
          status:
            filter.indexOf(option.value) === -1
              ? [...Array.from(new Set([...filter, option.value]))]
              : filter.filter((v) => v !== option.value),
        });
      } else {
        const flattened = flattenValues(option.options);
        setFilter({
          status: !isChecked(option)
            ? [...Array.from(new Set([...filter, ...flattened]))]
            : filter.filter((v) => flattened.indexOf(v) === -1),
        });
      }
    }
  };

  const isChecked = (option: StatusFilterOption): boolean => {
    if (!Array.isArray(option.options)) {
      return filter.indexOf(option.value) !== -1;
    }

    return option.options.map(isChecked).reduce((acc, o) => acc && o);
  };

  const isIndeterminate = (option: StatusFilterOption): boolean | undefined => {
    if (!Array.isArray(option.options)) {
      return;
    }

    const flattened = flattenValues(option.options);
    const checked = flattened.filter((o) => filter.indexOf(o) !== -1).length;
    return checked > 0 && checked !== flattened.length;
  };

  const renderTree = (options: StatusFilterOption[]) =>
    options.map((option) => (
      <TreeItem
        classes={{
          root: classes.item,
          selected: classes.selected,
          content: classes.content,
          label: classes.label,
          group: classes.group,
        }}
        key={option.value}
        nodeId={option.value}
        label={
          Array.isArray(option.options) ? (
            <Box display="flex" alignItems="center">
              <CheckboxBase
                value={option.value}
                checked={isChecked(option)}
                indeterminate={isIndeterminate(option)}
                onClick={(e) => {
                  e.stopPropagation();
                  selectOption(option);
                }}
              />
              <FormLabel className={classes.formLabel}>{option.label}</FormLabel>
            </Box>
          ) : (
            <Checkbox
              checked={isChecked(option)}
              indeterminate={isIndeterminate(option)}
              label={option.label}
              value={option.value}
              onClick={(e) => {
                selectOption(option);
                e.preventDefault();
              }}
            />
          )
        }
      >
        {Array.isArray(option.options) ? renderTree(option.options) : null}
      </TreeItem>
    ));

  return (
    <Box flex="1" ml={-1} maxWidth="100%" mt={-2}>
      <TreeView
        defaultCollapseIcon={<ArrowDropDownIcon color="inherit" />}
        defaultExpandIcon={<ArrowRightIcon color="inherit" />}
        multiSelect
        expanded={expanded}
        selected={filter}
        onNodeToggle={(e, nodes) => setExpanded(nodes)}
      >
        {renderTree(options)}
      </TreeView>
    </Box>
  );
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    item: {
      marginTop: theme.spacing(2),
      "&:focus > $content $label, &$selected:focus > $content $label": {
        backgroundColor: "transparent",
      },
      "&$selected > $content $label": {
        backgroundColor: "transparent",
        "&:hover": {
          backgroundColor: "transparent",
        },
      },
    },
    group: {
      marginLeft: theme.spacing(5),
    },
    selected: {},
    content: {
      cursor: "inherit",
    },
    label: {
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    formLabel: {
      fontSize: 16,
      fontWeight: "normal",
      lineHeight: 1,
      paddingTop: 1,
      color: theme.palette.dark.main,
      verticalAlign: "middle",
      cursor: "pointer",
      display: "inline-block",

      marginLeft: theme.spacing(2),
    },
  }),
);
